import { type MantineThemeOverride, rem } from '@mantine/core';

const SPACING: MantineThemeOverride['spacing'] = {
  // Sizes that match our current css variables
  px: rem(1),
  half: rem(2),
  '1': rem(4),
  '1_half': rem(6),
  '2': rem(8),
  '2_half': rem(10),
  '3': rem(12),
  '4': rem(16),
  '5': rem(20),
  '6': rem(24),
  '6_half': rem(28),
  '8': rem(32),
  '9': rem(36),
  '10': rem(40),
  '12': rem(48),
};

// Create the custom spacing type from the actual spacing object
export type TCustomMantineSpacing = keyof typeof SPACING;

// Define breakpoint keys for responsive values
export type TBreakpointKey = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

// Define responsive value type that can be either a single value or an object with breakpoint keys
export type TResponsiveValue<T> = T | Partial<Record<TBreakpointKey, T>>;

// Create responsive spacing type that supports both single values and responsive objects
export type TCustomMantineSpacingResponsive = TResponsiveValue<TCustomMantineSpacing>;

/**
 * Responsive spacing usage examples:
 *
 * // Single value (works as before)
 * <Text m="4">Single spacing</Text>
 *
 * // Responsive object - different spacing at different breakpoints
 * <Text m={{ xs: '2', sm: '4', md: '6', lg: '8' }}>Responsive spacing</Text>
 *
 * // Partial responsive object - only specify some breakpoints
 * <Text p={{ xs: '1', md: '4' }}>Partial responsive spacing</Text>
 *
 * // All spacing props support responsive values
 * <Text
 *   m={{ xs: '2', md: '4' }}
 *   p={{ xs: '1', lg: '6' }}
 *   mt={{ xs: '1', sm: '2' }}
 * >
 *   Multiple responsive props
 * </Text>
 */
export const COLORS: NonNullable<MantineThemeOverride['colors']> = {
  // Neutral
  neutral: [
    '#fafafa',
    '#f5f5f5',
    '#e5e5e5',
    '#d4d4d4',
    '#a1a1a1',
    '#737373',
    '#737373',
    '#404040',
    '#262626',
    '#171717',
  ],

  // Red
  red: [
    '#fef2f2',
    '#fee2e2',
    '#fecaca',
    '#fca5a5',
    '#f87171',
    '#ef4444',
    '#dc2626',
    '#b91c1c',
    '#991b1b',
    '#7f1d1d',
  ],

  // Orange
  orange: [
    '#fff7ed',
    '#ffedd5',
    '#fed7aa',
    '#fdba74',
    '#fb923c',
    '#f97316',
    '#ea580c',
    '#c2410c',
    '#9a3412',
    '#7c2d12',
  ],

  // Amber
  amber: [
    '#fffbeb',
    '#fef3c7',
    '#fde68a',
    '#fcd34d',
    '#fbbf24',
    '#f59e0b',
    '#d97706',
    '#b45309',
    '#92400e',
    '#78350f',
  ],

  // Yellow
  yellow: [
    '#fefce8',
    '#fef9c3',
    '#fef08a',
    '#fde047',
    '#facc15',
    '#eab308',
    '#ca8a04',
    '#a16207',
    '#854d0e',
    '#713f12',
  ],

  // Lime
  lime: [
    '#f7fee7',
    '#ecfccb',
    '#d9f99d',
    '#bef264',
    '#a3e635',
    '#84cc16',
    '#65a30d',
    '#4d7c0f',
    '#3f6212',
    '#365314',
  ],

  // Green
  green: [
    '#f0fdf4',
    '#dcfce7',
    '#bbf7d0',
    '#86efac',
    '#4ade80',
    '#22c55e',
    '#16a34a',
    '#15803d',
    '#166534',
    '#14532d',
  ],

  // Emerald
  emerald: [
    '#ecfdf5',
    '#d1fae5',
    '#a7f3d0',
    '#6ee7b7',
    '#34d399',
    '#10b981',
    '#059669',
    '#047857',
    '#065f46',
    '#064e3b',
  ],

  // Teal
  teal: [
    '#f0fdfa',
    '#ccfbf1',
    '#99f6e4',
    '#5eead4',
    '#2dd4bf',
    '#14b8a6',
    '#0d9488',
    '#0f766e',
    '#115e59',
    '#134e4a',
  ],

  // Cyan (brand)
  cyan: [
    '#ecfeff',
    '#cffafe',
    '#a5f3fc',
    '#67e8f9',
    '#22d3ee',
    '#06b6d4',
    '#0891b2',
    '#0e7490',
    '#155e75',
    '#164e63',
  ],

  // Sky
  sky: [
    '#f0f9ff',
    '#e0f2fe',
    '#bae6fd',
    '#7dd3fc',
    '#38bdf8',
    '#0ea5e9',
    '#0284c7',
    '#0369a1',
    '#075985',
    '#0c4a6e',
  ],

  // Blue
  blue: [
    '#eff6ff',
    '#dbeafe',
    '#bfdbfe',
    '#93c5fd',
    '#60a5fa',
    '#3b82f6',
    '#2563eb',
    '#1d4ed8',
    '#1e40af',
    '#1e3a8a',
  ],

  // Indigo
  indigo: [
    '#eef2ff',
    '#e0e7ff',
    '#c7d2fe',
    '#a5b4fc',
    '#818cf8',
    '#6366f1',
    '#4f46e5',
    '#4338ca',
    '#3730a3',
    '#312e81',
  ],

  // Violet
  violet: [
    '#f5f3ff',
    '#ede9fe',
    '#ddd6fe',
    '#c4b5fd',
    '#a78bfa',
    '#8b5cf6',
    '#7c3aed',
    '#6d28d9',
    '#5b21b6',
    '#4c1d95',
  ],

  // Purple
  purple: [
    '#faf5ff',
    '#f3e8ff',
    '#e9d5ff',
    '#d8b4fe',
    '#c084fc',
    '#a855f7',
    '#9333ea',
    '#7e22ce',
    '#6b21a8',
    '#581c87',
  ],

  // Fuchsia
  fuchsia: [
    '#fdf4ff',
    '#fae8ff',
    '#f5d0fe',
    '#f0abfc',
    '#e879f9',
    '#d946ef',
    '#c026d3',
    '#a21caf',
    '#86198f',
    '#701a75',
  ],

  // Pink
  pink: [
    '#fdf2f8',
    '#fce7f3',
    '#fbcfe8',
    '#f9a8d4',
    '#f472b6',
    '#ec4899',
    '#db2777',
    '#be185d',
    '#9d174d',
    '#831843',
  ],

  // Rose
  rose: [
    '#fff1f2',
    '#ffe4e6',
    '#fecdd3',
    '#fda4af',
    '#fb7185',
    '#f43f5e',
    '#e11d48',
    '#be123c',
    '#9f1239',
    '#881337',
  ],
};

// Create a type that represents dot notation for color shades (e.g., 'blue.6')
type TColorShade = `${keyof typeof COLORS}.${0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`;

// Combined color type that includes color names, color shades with dot notation, and custom strings
export type TColor = keyof typeof COLORS | TColorShade | string;

/** We don't want to allow overriding all the style props, but these are the ones we want to globally allow */
export interface IMantineStyleProps {
  m?: TCustomMantineSpacingResponsive;
  mt?: TCustomMantineSpacingResponsive;
  mb?: TCustomMantineSpacingResponsive;
  ml?: TCustomMantineSpacingResponsive;
  mr?: TCustomMantineSpacingResponsive;
  mx?: TCustomMantineSpacingResponsive;
  my?: TCustomMantineSpacingResponsive;
  p?: TCustomMantineSpacingResponsive;
  pt?: TCustomMantineSpacingResponsive;
  pb?: TCustomMantineSpacingResponsive;
  pl?: TCustomMantineSpacingResponsive;
  pr?: TCustomMantineSpacingResponsive;
  px?: TCustomMantineSpacingResponsive;
  py?: TCustomMantineSpacingResponsive;
  c?: TColor;
}

export const THEME: MantineThemeOverride = {
  fontFamily: 'FK Grotesk, sans-serif',
  spacing: SPACING,
  colors: COLORS,
};
